"use client";

/**
 * The foot of every portal page: how to reach a human.
 *
 * It was two underlined links in a wrapping row, left-aligned under whatever
 * the page happened to end with — prose styling on things that are not prose,
 * with no space above them and nothing saying what they were for. On a phone,
 * where they are the only way to reach anyone, they read as two words left
 * behind rather than as the page's ending.
 *
 * WHAT MAKES IT AN ENDING is the space above it and the centring, not a rule.
 * A drawn line is what the surface rules refuse on a pane, and the same
 * argument holds here: a footer separated by a hairline reads as a row that
 * got cut off, while `section` of air reads as the page finishing. Centred
 * because it is the one block on these screens that belongs to no column —
 * every other thing lines up with the content, and this sits under all of it.
 *
 * THE CONTROLS ARE THE HEADER'S. Home's page header carries the same pair as
 * buttons past rail width, so the footer hides them there rather than offering
 * the inbox twice on one screen; every other page has no header actions and
 * keeps them at every width. Same components either way — a link that looks
 * like a button in one place and like prose in another is two designs for one
 * decision.
 */

import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { Hint } from "@/components/ui/design-system/text";
import { cn } from "@/lib/utils";
import { Quiet, Secondary } from "./one/ui";

/**
 * Footer scale: auto width so two sit side by side instead of each taking the
 * row, and the standard control height rather than the 52 a phone gives the
 * one action a screen is for. These are the way out of the page, not the
 * point of it.
 */
const FOOT_ACTION =
  "h-nf-ds-control w-auto px-nf-ds-4 text-nf-ds-sm lg:h-nf-ds-control lg:px-nf-ds-4 lg:text-nf-ds-sm";

export function PortalFooter({
  supportEmail,
  signedIn,
  inboxHref,
  signInHref,
  /** Home puts this pair in its page header past `lg`. */
  hideHelpOnDesktop,
}: {
  supportEmail: string;
  signedIn: boolean;
  /** Null while already on the inbox — a door to the room you are in. */
  inboxHref: string | null;
  signInHref: string;
  hideHelpOnDesktop: boolean;
}) {
  const router = useRouter();
  const t = useTranslations("shell");

  /*
    `block` on top, not `section`. The scroll area already ends with its own
    padding, so a section gap on top of that put the better part of a hundred
    points between the last card and the question — which reads as the page
    having stopped rather than ended.
  */
  return (
    <footer className="mx-auto max-w-6xl px-nf-ds-6 pt-nf-ds-block pb-nf-ds-8">
      {signedIn ? (
        <div
          className={cn(
            "flex flex-col items-center gap-nf-ds-tight",
            hideHelpOnDesktop && "lg:hidden",
          )}
        >
          <Hint muted>{t("footer.needHand")}</Hint>
          <div className="flex items-center gap-nf-ds-2">
            {/* The header's pair exactly: the ring on the one that reaches
                a person here, nothing on the one that leaves for mail. Two
                identical quiet labels were the old footer again — centred
                and captioned, but still two words the eye cannot tell apart.

                A button pushed through the router rather than an anchor,
                because an `<a href>` to an app route is a full page load. */}
            {inboxHref ? (
              <Secondary
                onClick={() => router.push(inboxHref)}
                className={FOOT_ACTION}
              >
                {t("footer.messageUs")}
              </Secondary>
            ) : null}
            <Quiet href={`mailto:${supportEmail}`} className={FOOT_ACTION}>
              {t("footer.support")}
            </Quiet>
          </div>
        </div>
      ) : (
        <div className="flex justify-center">
          <Secondary
            onClick={() => router.push(signInHref)}
            className={FOOT_ACTION}
          >
            {t("footer.signIn")}
          </Secondary>
        </div>
      )}
    </footer>
  );
}
